import { Skeleton } from "antd";
import { NotificationResponseModel } from "api/merchantRepository/notification/model/NotificationResponseModel";
import emptyList from "assets/images/mobile.png";
import { higherOrderComponent } from "components/higherOrderComponent/HigherOrderComponent";
import { isEmpty } from "lodash";
import { createRef } from "react";
import { IoNotifications } from "react-icons/io5";
import { ClassFormat } from "utils/format/ClassFormat";
import { colorFormat } from "utils/format/ColorFormat";
import { InjectionToken } from "utils/injectionToken/InjectionToken";
import { strings } from "utils/localizedStrings";
import Transition from "utils/Transition";
import { INotificationState } from "../interface/INotification";
import { NotificationViewModel } from "../viewModel/NotificationViewModel";
import { BaseViews, IBaseViews } from "app/based/baseViews/BaseViews";

class NotificationFeature extends BaseViews<
  NotificationViewModel,
  {},
  INotificationState
> {
  private trigger = createRef<HTMLButtonElement>();
  private dropdown = createRef<HTMLDivElement>();
  private listContainer = createRef<HTMLDivElement>();

  constructor(props: IBaseViews) {
    super(props);
    this.state = {
      dropdownOpen: false,
      notifications: [],
      loading: false,
      pagination: {
        page: 0,
        limit: 20,
        total: 0,
      },
    };
  }

  protected override bindViewModel(): void {
    super.bindViewModel();

    this.viewModel.output.savedNoti
      .subscribe((list: NotificationResponseModel[]) => {
        this.setState((prev) => ({
          notifications: [...prev.notifications, ...list],
        }));
      })
      .add(this.viewModel.subscriptions);

    this.viewModel.input.loading
      .subscribe((res) => {
        this.setState({
          loading: res,
        });
      })
      .add(this.viewModel.subscriptions);

    this.viewModel.output.pagination
      .subscribe((p) => {
        this.setState((prev) => ({
          pagination: { ...prev.pagination, ...p },
        }));
      })
      .add(this.viewModel.subscriptions);
  }

  componentDidMount(): void {
    super.componentDidMount();
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("keydown", this.handleEsc);
    if (this.listContainer.current) {
      this.listContainer.current.addEventListener("scroll", this.handleScroll);
    }
  }

  protected override dispose(): void {
    super.dispose();
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleEsc);
    if (this.listContainer.current) {
      this.listContainer.current.removeEventListener(
        "scroll",
        this.handleScroll
      );
    }
  }

  private handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    if (
      !this.state.dropdownOpen ||
      this.dropdown.current?.contains(target) ||
      this.trigger.current?.contains(target)
    ) {
      return;
    }
    this.setState({ dropdownOpen: false });
  };

  private handleEsc = (e: KeyboardEvent) => {
    if (!this.state.dropdownOpen || e.key !== "Escape") return;
    this.setState({ dropdownOpen: false });
  };

  private handleScroll = () => {
    const container = this.listContainer.current;
    if (!container) return;

    const { scrollTop, clientHeight, scrollHeight } = container;
    const { notifications, pagination } = this.state;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      if (notifications.length < (pagination.total ?? 0)) {
        const nextPage = (pagination.page ?? 0) + 1;
        this.setState({
          pagination: { ...pagination, page: nextPage },
        });
        this.viewModel.input.fetchNoti.next({
          page: nextPage,
          limit: pagination.limit ?? 20,
        });
      }
    }
  };

  private toggleDropdown = () => {
    const willOpen = !this.state.dropdownOpen;

    if (willOpen) {
      this.setState(
        {
          dropdownOpen: true,
          notifications: [],
          pagination: { page: 0, limit: 20, total: 0 },
        },
        () => {
          this.viewModel.input.fetchNoti.next({
            page: 0,
            limit: 20,
          });
        }
      );
    } else {
      this.setState({ dropdownOpen: false });
    }
  };

  render() {
    const { dropdownOpen, notifications, pagination, loading } = this.state;

    return (
      <div className="relative inline-flex ml-3">
        <button
          ref={this.trigger}
          className={`w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition duration-150 rounded-full ${
            dropdownOpen && "bg-gray-200"
          }`}
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
          onClick={this.toggleDropdown}
        >
          <IoNotifications
            style={{
              color: dropdownOpen ? colorFormat.Blue : colorFormat.Black,
            }}
          />
        </button>

        <Transition
          className="origin-top-right z-10 absolute top-full xl:right-0 -right-[205px] lg:w-[600px] min-w-80 bg-white border border-gray-200 rounded-[18px] shadow-lg overflow-hidden mt-2"
          show={dropdownOpen}
          enter="transition ease-out duration-200 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          appear={undefined}
        >
          <div
            ref={this.dropdown}
            onFocus={() => this.setState({ dropdownOpen: true })}
            onBlur={() => this.setState({ dropdownOpen: false })}
          >
            <div className="py-3 text-center text-[16px] font-[700] border-b border-gray-200">
              {strings.GlobalLabels.Noti}
            </div>
            <div
              ref={this.listContainer}
              className={`max-h-[420px] ${
                isEmpty(notifications) ? "overflow-hidden" : "overflow-y-auto"
              } px-4 py-1`}
            >
              {loading ? (
                <div className="flex justify-center items-center w-full">
                  <Skeleton loading={loading} paragraph={{ rows: 3 }} />
                </div>
              ) : (
                <>
                  {isEmpty(notifications) ? (
                    <section className="flex flex-col gap-2 justify-center items-center w-full h-[170px]">
                      <img
                        src={emptyList}
                        className="object-contain w-full h-[130px] ml-3"
                        loading="lazy"
                        alt="empty-noti"
                      />
                      <p className={ClassFormat.topupInfoLabel}>
                        {strings.GlobalLabels.NoInfo}
                      </p>
                    </section>
                  ) : (
                    <></>
                  )}
                  {notifications.map((n, idx) => (
                    <div
                      key={n.code ?? idx}
                      className={`${
                        idx !== 0 ? "border-t border-gray-200" : ""
                      } py-2`}
                    >
                      <div
                        className={`${
                          idx === 0 ? "bg-blue-50" : ""
                        } rounded-[8px] px-3 py-2`}
                      >
                        <span className="text-[13px] text-[#111827]">
                          {n.message}
                        </span>
                      </div>
                    </div>
                  ))}
                  {notifications.length < (pagination.total ?? 0) && (
                    <div className="text-center py-2 text-sm text-gray-400">
                      Loading...
                    </div>
                  )}
                </>
              )}
            </div>
            {!isEmpty(notifications) ? (
              <div className="flex justify-end pb-2 pr-2">
                <div className="w-0 h-0 border-l-8 border-l-transparent border-t-8 border-t-gray-300 border-r-8 border-r-transparent rounded-sm rotate-180" />
              </div>
            ) : (
              <></>
            )}
          </div>
        </Transition>
      </div>
    );
  }
}

export default higherOrderComponent(NotificationFeature, {
  token: InjectionToken.NotificationViewModel,
});
